import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { useTranslation } from "react-i18next";

import ViewContainer from "../../../components/ViewContainer";
import DataTable from "../../../components/DataTable";
import PanelFootnote from "../../../components/PanelFootnote";
import SubgroupFilterBar, { useSubgroupFilter } from "./SubgroupFilterBar";
import { brand } from "../../../themes/theme";

import charData from "../../../data/pharma/subgroupsCharacterization.json";

// Cajita de color por tipo de fuente. A diferencia de una etiqueta generica
// ("Cuestionario"/"Autorreporte"), cada cajita lleva el propio contenido de
// "como se captura" (p.ej. "GAD-7" o "free-text self-report"): cada trozo del
// dato metido en su cajita de color, no un rotulo aparte encima de un texto.
// Cuestionario/base en gris, autorreporte activo en azul, dato pasivo en naranja.
const SOURCE_TYPE_STYLES = {
    questionnaire: { bg: brand.gray100, color: brand.gray600 },
    selfReport: { bg: "rgba(50,72,202,0.08)", color: brand.primary },
    passive: { bg: "rgba(255,130,57,0.09)", color: brand.accentMedium },
};

function CapturaChip({ type, text }) {
    const style = SOURCE_TYPE_STYLES[type] || SOURCE_TYPE_STYLES.selfReport;
    return (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", px: 0.9, py: 0.25, borderRadius: "5px", bgcolor: style.bg }}>
            <Typography sx={{ fontSize: 11, fontWeight: 500, color: style.color, lineHeight: 1.3 }}>{text}</Typography>
        </Box>
    );
}

// Modulo 3.2.1 — Subgroups · Characterization. Contenido del Excel v2
// (Modulo Caracterizacion de subgrupos_Mential_v2), 4 columnas por elemento.
// El filtro de subgrupo (compartido con el resto de Subgroups) cambia el
// valor de "Medida" mostrado; con "Todos los subgrupos" se ve el rango
// (min–max) entre los 6, para que la diferencia sea visible sin filtrar.
// Acordeon (una categoria abierta a la vez): 6 categorias x hasta 6 filas x
// 4 campos es demasiado para mostrar todo junto sin que se sienta plano y
// abrumador; con el filtro de subgrupo, tampoco es tan urgente ver siempre
// las 6 a la vez.
function SubgroupsCharacterizationView() {
    const { t } = useTranslation();
    const { selected, filtered } = useSubgroupFilter();
    const [openKey, setOpenKey] = useState(charData.categories[0]?.key || null);
    const tbl = (key) => t(`views.pharmaSubgroupsCharacterization.table.${key}`);

    const formatMedida = (row) => {
        if (row.qualitative) return t("views.pharmaSubgroupsCharacterization.qualitativeMeasure");
        const unit = row.unit || "";
        if (selected !== "all") {
            const v = row.medidaBySubgroup[selected];
            return `${v}${unit}`;
        }
        const values = filtered.map((s) => row.medidaBySubgroup[s.id]);
        const min = Math.min(...values);
        const max = Math.max(...values);
        return min === max ? `${min}${unit}` : `${min}–${max}${unit}`;
    };

    const columns = [
        { key: "elemento", label: tbl("elemento"), width: "1.5fr" },
        { key: "medida", label: tbl("medida"), width: "0.9fr" },
        { key: "captura", label: tbl("captura"), width: "1.7fr" },
        { key: "cadencia", label: tbl("cadencia"), width: "1.1fr" },
    ];

    return (
        <ViewContainer title={t("views.pharmaSubgroupsCharacterization.title")} description={t("views.pharmaSubgroupsCharacterization.description")}>
            <SubgroupFilterBar />
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1.25 }}>
                {charData.categories.map((cat) => {
                    const open = openKey === cat.key;
                    return (
                        <Box
                            key={cat.key}
                            sx={{
                                flexShrink: 0,
                                borderRadius: "14px",
                                border: "1px solid",
                                borderColor: open ? brand.primary : brand.gray100,
                                bgcolor: "#fff",
                                overflow: "hidden",
                                transition: "border-color 0.2s ease",
                            }}
                        >
                            <Box
                                component="button"
                                type="button"
                                onClick={() => setOpenKey(open ? null : cat.key)}
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1.5,
                                    px: 2,
                                    py: 1.5,
                                    border: 0,
                                    bgcolor: open ? "rgba(50,72,202,0.045)" : "transparent",
                                    cursor: "pointer",
                                    textAlign: "left",
                                    fontFamily: "inherit",
                                    transition: "background-color 0.2s ease",
                                    "&:hover": { bgcolor: open ? "rgba(50,72,202,0.06)" : brand.gray50 },
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 15, color: "text.primary" }}>{cat.title}</Typography>
                                    <Typography sx={{ fontSize: 11, color: "text.secondary", flexShrink: 0 }}>
                                        {t("views.pharmaSubgroupsCharacterization.itemsCount", { count: cat.rows.length })}
                                    </Typography>
                                </Box>
                                <ExpandMoreRoundedIcon
                                    sx={{
                                        color: open ? "primary.main" : "text.secondary",
                                        transform: open ? "rotate(180deg)" : "rotate(0deg)",
                                        transition: "transform 0.25s ease",
                                        flexShrink: 0,
                                    }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateRows: open ? "1fr" : "0fr",
                                    transition: "grid-template-rows 0.28s ease",
                                }}
                            >
                                <Box sx={{ overflow: "hidden", minHeight: 0 }}>
                                    <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
                                        <DataTable
                                            dense
                                            columns={columns}
                                            rows={cat.rows.map((row) => ({
                                                elemento: row.elemento,
                                                medida: (
                                                    <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: row.qualitative ? "text.secondary" : "primary.main" }}>
                                                        {formatMedida(row)}
                                                    </Typography>
                                                ),
                                                captura: (
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
                                                        {row.capturaParts.map((part, i) => (
                                                            <CapturaChip key={i} type={part.type} text={part.text} />
                                                        ))}
                                                    </Box>
                                                ),
                                                cadencia: row.cadencia,
                                            }))}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            <PanelFootnote sx={{ flexShrink: 0 }}>{t("views.pharmaSubgroupsCharacterization.footnote")}</PanelFootnote>
        </ViewContainer>
    );
}

export default SubgroupsCharacterizationView;
